(() => {
  const openDialog = (dialog, trigger) => {
    if (!dialog || dialog.open) return;
    const returnTarget = trigger?.closest('.hx-mobile-menu')?.querySelector(':scope > summary') || trigger;
    document.querySelectorAll('.hx-menu[open],.hx-mobile-menu[open]').forEach(menu => menu.open = false);
    dialog.showModal(); document.documentElement.classList.add('hx-modal-open');
    dialog.querySelector('input[type="search"]')?.focus();
    dialog.dispatchEvent(new Event('hexo:dialog-open'));
    dialog.addEventListener('close', () => { document.documentElement.classList.remove('hx-modal-open'); returnTarget?.focus(); }, { once: true });
  };
  const rails = new Map();
  document.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const dialog = event.target.closest?.('.hx-dialog[open]');
    if (!dialog) return;
    const controls = [...dialog.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')]
      .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
    const target = event.shiftKey && document.activeElement === controls[0] ? controls.at(-1)
      : !event.shiftKey && document.activeElement === controls.at(-1) ? controls[0] : null;
    if (target) { event.preventDefault(); target.focus(); }
  });
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const motionReduced = () => reducedMotion.matches || document.documentElement.hasAttribute('data-hx-reduce-motion');
  const revealTargets = [
    '.hx-category-grid > .hx-category', '.hx-product-rail > *',
    '.hx-placement-photo',
    '.hx-guide-edit-links > a', '.hx-document-shell--guide .split-image',
    '.hx-document-shell--story .split-image'
  ].join(',');
  const revealSeen = new WeakSet();
  const revealPending = new Set();
  const revealActive = new Map();
  const revealRecipe = element => {
    if (element.matches('.hx-placement-photo')) return {
      target: element.querySelector('img'), duration: 620,
      frames: [{ opacity: 0, transform: 'scale(1.035)' }, { opacity: 1, transform: 'scale(1)' }]
    };
    if (element.parentElement.matches('.hx-product-rail,.hx-guide-edit-links')) return {
      target: element, duration: 620,
      frames: [{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }]
    };
    return {
      target: element, duration: 620,
      frames: [{ opacity: 0, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }]
    };
  };
  const settleReveal = element => {
    if (!element) return;
    revealObserver?.unobserve(element);
    revealPending.delete(element);
    const animation = revealActive.get(element);
    if (animation) { revealActive.delete(element); animation.cancel(); }
    element.dataset.hxReveal = 'complete';
  };
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting || entry.intersectionRatio < .16) return;
      const element = entry.target;
      if (motionReduced() || document.hidden || !element.isConnected || element.contains(document.activeElement)) {
        settleReveal(element);
        return;
      }
      const animation = revealActive.get(element);
      if (!animation) { settleReveal(element); return; }
      revealObserver.unobserve(element);
      revealPending.delete(element);
      element.dataset.hxReveal = 'running';
      animation.play();
    });
  }, { threshold: .16, rootMargin: `0px 0px -${Math.round(innerHeight * .12)}px 0px` }) : null;
  const syncReveals = () => {
    for (const element of [...revealPending, ...revealActive.keys()]) {
      if (!element.isConnected) settleReveal(element);
    }
    document.querySelectorAll(revealTargets).forEach(element => {
      if (revealSeen.has(element)) return;
      revealSeen.add(element);
      const rect = element.getBoundingClientRect();
      const recipe = revealRecipe(element);
      if (!revealObserver || motionReduced() || !recipe.target?.animate || element.contains(document.activeElement) ||
          (rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth)) {
        element.dataset.hxReveal = 'complete';
        return;
      }
      const row = [...element.parentElement.children].filter(sibling =>
        sibling.matches(revealTargets) && Math.abs(sibling.offsetTop - element.offsetTop) < 4);
      const delay = Math.min(Math.max(0, row.indexOf(element)) * 80, 240);
      const animation = recipe.target.animate(recipe.frames,
        { duration: recipe.duration, delay, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' });
      animation.pause();
      animation.currentTime = 0;
      revealActive.set(element, animation);
      animation.finished.then(() => {
        if (revealActive.get(element) === animation) settleReveal(element);
      }, () => {});
      element.dataset.hxReveal = 'ready';
      revealPending.add(element);
      revealObserver.observe(element);
    });
  };
  const revealInteraction = event => {
    for (const element of [...revealPending, ...revealActive.keys()]) {
      if (element.contains(event.target)) settleReveal(element);
    }
  };
  document.addEventListener('focusin', revealInteraction);
  document.addEventListener('pointerdown', revealInteraction, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) for (const element of revealActive.keys()) {
      if (element.dataset.hxReveal === 'running') settleReveal(element);
    }
  });
  const updateRevealPreference = () => {
    if (motionReduced()) {
      for (const element of [...revealPending, ...revealActive.keys()]) settleReveal(element);
    }
  };
  reducedMotion.addEventListener('change', updateRevealPreference);
  document.addEventListener('hexo:accessibility-change', updateRevealPreference);
  const depthFrames = new Map();
  const depthRanges = new WeakMap();
  const depthVisible = new Set();
  let depthFrame = 0;
  const resetDepth = frame => {
    depthFrames.get(frame)?.cancel();
    depthFrames.set(frame, null);
    frame.style.removeProperty('--sc-p');
    frame.style.removeProperty('--hx-depth-range');
    frame.dataset.scDepth = 'static';
  };
  const updateDepth = () => {
    depthFrame = 0;
    if (motionReduced() || document.hidden) return;
    const updates = [];
    const headerBottom = Math.max(0, document.querySelector('.hx-header')?.getBoundingClientRect().bottom || 0);
    const visibleHeight = Math.max(1, innerHeight - headerBottom);
    for (const frame of depthVisible) {
      if (!frame.isConnected) continue;
      const rect = frame.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= innerHeight || !rect.height) continue;
      updates.push([frame,
        Math.max(0, Math.min(1, (innerHeight - rect.top) / (visibleHeight + rect.height))),
        Math.min(64, rect.height * .12)]);
    }
    for (const [frame, progress, range] of updates) {
      let animation = depthFrames.get(frame);
      if (!animation || depthRanges.get(frame) !== range) {
        const image = frame.querySelector('img');
        if (!image?.animate) continue;
        animation?.cancel();
        animation = image.animate([
          { transform: `translateY(${-range}px)` },
          { transform: `translateY(${range}px)` }
        ], { duration: 1000, fill: 'both', easing: 'linear' });
        animation.pause();
        depthFrames.set(frame, animation);
        depthRanges.set(frame, range);
      }
      frame.style.setProperty('--hx-depth-range', `${range}px`);
      frame.style.setProperty('--sc-p', progress.toFixed(4));
      frame.dataset.scDepth = 'active';
      animation.currentTime = progress * 1000;
    }
  };
  const requestDepth = () => {
    if (!depthFrame && depthVisible.size && !motionReduced() && !document.hidden) {
      depthFrame = requestAnimationFrame(updateDepth);
    }
  };
  const depthObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(({ target, isIntersecting }) => {
      if (isIntersecting) depthVisible.add(target);
      else depthVisible.delete(target);
    });
    requestDepth();
  }) : null;
  const removeDepth = frame => {
    resetDepth(frame);
    depthObserver?.unobserve(frame);
    depthVisible.delete(frame);
    depthFrames.delete(frame);
  };
  const syncDepth = () => {
    for (const frame of depthFrames.keys()) if (!frame.isConnected) removeDepth(frame);
    if (!depthObserver) return;
    document.querySelectorAll('.hx-story-image').forEach(frame => {
      if (depthFrames.has(frame)) return;
      depthFrames.set(frame, null);
      frame.dataset.scDepth = 'static';
      depthObserver.observe(frame);
    });
    requestDepth();
  };
  const pauseDepth = () => {
    cancelAnimationFrame(depthFrame);
    depthFrame = 0;
    for (const frame of depthFrames.keys()) resetDepth(frame);
  };
  document.addEventListener('scroll', requestDepth, { passive: true, capture: true });
  window.addEventListener('resize', requestDepth, { passive: true });
  const updateDepthPreference = () => motionReduced() ? pauseDepth() : requestDepth();
  reducedMotion.addEventListener('change', updateDepthPreference);
  document.addEventListener('hexo:accessibility-change', updateDepthPreference);
  document.addEventListener('visibilitychange', () => document.hidden ? pauseDepth() : requestDepth());
  const placementAnimations = new Map();
  const settlePlacement = image => {
    placementAnimations.get(image)?.cancel();
    placementAnimations.delete(image);
  };
  const settlePlacements = () => {
    for (const image of placementAnimations.keys()) settlePlacement(image);
  };
  const updatePlacementPreference = () => { if (motionReduced()) settlePlacements(); };
  reducedMotion.addEventListener('change', updatePlacementPreference);
  document.addEventListener('hexo:accessibility-change', updatePlacementPreference);
  document.addEventListener('visibilitychange', () => { if (document.hidden) settlePlacements(); });
  const previewPlacement = (event) => {
    const link = event.target.closest?.('[data-hx-placement-src]');
    if (!link || (event.type === 'pointerover' && event.pointerType === 'touch')) return;
    const section = link.closest('.hx-placement');
    const image = section?.querySelector('[data-hx-placement-image]');
    const caption = section?.querySelector('[data-hx-placement-caption]');
    if (!image || !caption || image.dataset.previewSource === link.dataset.hxPlacementSrc) return;
    const source = link.dataset.hxPlacementSrc;
    const title = link.dataset.hxPlacementTitle;
    image.dataset.previewSource = source;
    const preview = new Image();
    preview.onload = () => {
      if (!image.isConnected || image.dataset.previewSource !== source) return;
      settleReveal(image.closest('.hx-placement-photo'));
      settlePlacement(image);
      image.src = source;
      image.alt = title;
      caption.textContent = title;
      if (!motionReduced() && !document.hidden && image.animate) {
        const animation = image.animate([{ opacity: .5 }, { opacity: 1 }], { duration: 240, easing: 'ease-out' });
        placementAnimations.set(image, animation);
        animation.finished.then(() => {
          if (placementAnimations.get(image) === animation) settlePlacement(image);
        }, () => {});
      }
    };
    preview.onerror = () => { if (image.dataset.previewSource === source) delete image.dataset.previewSource; };
    preview.src = source;
  };
  document.addEventListener('pointerover', previewPlacement);
  document.addEventListener('focusin', previewPlacement);
  const syncRails = () => {
    for (const [rail, cleanup] of rails) {
      if (!rail.isConnected) { cleanup(); rails.delete(rail); }
    }
    document.querySelectorAll('.hx-product-rail').forEach(rail => {
      if (rails.has(rail)) return;
      const controls = rail.closest('.hx-featured')?.querySelector('.hx-rail-controls');
      if (!controls) return;
      const update = () => {
        const bounds = rail.getBoundingClientRect();
        const first = rail.firstElementChild?.getBoundingClientRect();
        const last = rail.lastElementChild?.getBoundingClientRect();
        const rtl = getComputedStyle(rail).direction === 'rtl';
        const previous = controls.querySelector('[data-hx-rail="prev"]');
        const next = controls.querySelector('[data-hx-rail="next"]');
        if (previous) previous.disabled = !first || (rtl ? first.right <= bounds.right + 2 : first.left >= bounds.left - 2);
        if (next) next.disabled = !last || (rtl ? last.left >= bounds.left - 2 : last.right <= bounds.right + 2);
      };
      const observer = new ResizeObserver(update);
      observer.observe(rail);
      rail.addEventListener('scroll', update, { passive: true });
      rails.set(rail, () => { observer.disconnect(); rail.removeEventListener('scroll', update); });
      update();
    });
  };
  document.addEventListener('click', event => {
    // At Horizon's non-modal breakpoint the cart is a header popover, not a
    // permanent sidebar. Dismiss it without swallowing the shopper's next action.
    const cartDrawer = document.getElementById('cart-drawer');
    if (cartDrawer?.hasAttribute('open') && matchMedia('(min-width:990px)').matches &&
        !event.target.closest('#cart-drawer,[data-hx-cart]') && !document.querySelector('.hx-dialog[open]')) {
      const nextFocus = event.target.closest('a[href],button,input,select,textarea,[tabindex]');
      cartDrawer.close().then(() => {
        if (nextFocus?.isConnected && !document.querySelector('dialog[open]')) nextFocus.focus({ preventScroll: true });
      });
    }
    const close = event.target.closest('[data-hx-close]');
    if (close) close.closest('dialog').close();
    const dialog = event.target.closest('.hx-dialog');
    if (event.target === dialog) { const rect = dialog.getBoundingClientRect(); if(event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close(); }
    const trigger = event.target.closest('[data-hx-dialog]');
    if (trigger && !event.ctrlKey && !event.metaKey) { event.preventDefault(); document.dispatchEvent(new Event('hexo:wishlist')); openDialog(document.getElementById(trigger.dataset.hxDialog), trigger); }
    const cart = event.target.closest('[data-hx-cart]');
    if (cart && !event.ctrlKey && !event.metaKey) { const drawer = document.getElementById('cart-drawer'); if (typeof drawer?.toggle === 'function') { event.preventDefault(); drawer.toggle(); } }
    const design = event.target.closest('[data-hx-design]');
    if (design) { const viewer = document.getElementById('hexo-design'); viewer.querySelector('h2').textContent = design.dataset.title; const image = viewer.querySelector('img'); image.src = design.dataset.image; image.alt = design.dataset.title; openDialog(viewer, design); }
    const railButton = event.target.closest('[data-hx-rail]');
    if (railButton && !railButton.disabled) { const rail = railButton.closest('.hx-featured').querySelector('.hx-product-rail'); const direction = getComputedStyle(rail).direction === 'rtl' ? -1 : 1; const step = rail.clientWidth * direction * (railButton.dataset.hxRail === 'next' ? 1 : -1); rail.scrollBy({left:step,behavior:motionReduced() ? 'instant' : 'smooth'}); }
  });
  syncRails();
  syncReveals();
  syncDepth();
  document.addEventListener('shopify:section:load', syncRails);
  document.addEventListener('shopify:section:unload', syncRails);
  document.addEventListener('shopify:section:load', syncReveals);
  document.addEventListener('shopify:section:load', syncDepth);
  document.addEventListener('shopify:section:unload', event => {
    for (const frame of depthFrames.keys()) if (event.target.contains(frame)) removeDepth(frame);
    for (const image of placementAnimations.keys()) if (event.target.contains(image)) settlePlacement(image);
    for (const element of [...revealPending, ...revealActive.keys()]) {
      if (!element.isConnected || event.target.contains(element)) settleReveal(element);
    }
  });
  document.addEventListener('shopify:section:select', event => {
    for (const element of [...revealPending, ...revealActive.keys()]) {
      if (event.target.contains(element)) settleReveal(element);
    }
  });
})();
